import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiPlus,
  FiTrash2,
  FiSave,
  FiChevronDown,
  FiCopy,
  FiEye,
  FiX,
  FiLock,
  FiArrowLeft,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import TmisLayout from "../components/TmisLayout";
import "../PerformanceTemplate.css";

const API_BASE = process.env.REACT_APP_API || "http://localhost:3023/api";

const API_ENDPOINTS = {
  createTemplate: `${API_BASE}/createTemplate`,
  updateTemplate: (templateId) => `${API_BASE}/updateTemplate/${templateId}`,
  getTemplateDetails: (templateId) =>
    `${API_BASE}/getTemplateDetails/${templateId}`,
};

const element_TYPES = [
  { value: "radio", label: "Radio Buttons" },
  { value: "short_answer", label: "Short Answer" },
  { value: "long_answer", label: "Long Answer" },
  { value: "multiple_choice", label: "Multiple Choice" },
];

const makeOption = (label = "") => ({
  id: `${Date.now()}-${Math.random()}`,
  label,
});

const makeelement = () => ({
  id: `${Date.now()}-${Math.random()}`,
  element: "",
  type: "radio",
  required: true,
  options: [makeOption("Excellent"), makeOption("Good")],
});

const getLoginUser = () => {
  try {
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("Failed to read user:", error);
    return null;
  }
};

const getAuthToken = () => {
  return localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
};

const getAxiosConfig = () => {
  const token = getAuthToken();

  if (!token) return {};

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const normalizeelementTypeForBackend = (type) => {
  if (type === "multiple_choice") return "multiple";
  return type;
};

const UnauthorizedPage = () => {
  const navigate = useNavigate();

  return (
    <TmisLayout showSidebar={false}>
      <main className="unauthorized-main">
        <div className="unauthorized-card">
          <div className="lock-animation">
            <div className="lock-circle">
              <FiLock />
            </div>
            <span className="pulse-ring"></span>
            <span className="pulse-ring second"></span>
          </div>

          <h1>Access Denied</h1>
          <p>
            You are not authorized to view the Performance Template page.
            Please contact the administrator if you need access.
          </p>

          <button
            type="button"
            className="unauthorized-btn"
            onClick={() => navigate("/dashboard")}
          >
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </main>
    </TmisLayout>
  );
};

const PerformanceTemplate = () => {
  const loginUser = useMemo(() => getLoginUser(), []);
  const role = String(loginUser?.role || "").toLowerCase();

  if (role === "trainer") {
    return <UnauthorizedPage />;
  }

  return <PerformanceTemplateBuilder loginUser={loginUser} />;
};

const PerformanceTemplateBuilder = ({ loginUser }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const editMode = location.state?.mode === "edit";
  const editTemplateId = location.state?.template_id || null;

  const [templateTitle, setTemplateTitle] = useState("");
  const [description, setDescription] = useState("");
  const [elements, setelements] = useState([makeelement()]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const createdBy = loginUser?.id || null;

  const totalChoiceelements = useMemo(
    () =>
      elements.filter(
        (item) => item.type === "radio" || item.type === "multiple_choice"
      ).length,
    [elements]
  );

  const showMessage = (text, type = "warning") => {
    setMessage(text);
    setMessageType(type);
  };

  const loadEditTemplate = useCallback(async () => {
    if (!editMode || !editTemplateId) return;

    setLoadingEdit(true);
    setMessage("");
    setMessageType("");

    try {
      const response = await axios.get(
        API_ENDPOINTS.getTemplateDetails(editTemplateId),
        getAxiosConfig()
      );

      const template = response.data?.template || null;
      const rows = response.data?.questions || [];

      setTemplateTitle(template?.title || "");
      setDescription(template?.description || "");

      if (Array.isArray(rows) && rows.length > 0) {
        setelements(
          rows.map((item) => {
            const type =
              item.type === "multiple" ? "multiple_choice" : item.type || "radio";

            const answers = Array.isArray(item.answers) ? item.answers : [];

            return {
              id: `${Date.now()}-${Math.random()}`,
              element: item.element || item.question || "",
              type,
              required: Number(item.is_required) === 1,
              options:
                type === "radio" || type === "multiple_choice"
                  ? answers.length > 0
                    ? answers.map((answer) => makeOption(answer.answer || ""))
                    : [makeOption("Excellent"), makeOption("Good")]
                  : [],
            };
          })
        );
      } else {
        setelements([makeelement()]);
      }
    } catch (error) {
      console.error("Load edit template error:", error);
      showMessage("Failed to load template for edit.", "warning");
    } finally {
      setLoadingEdit(false);
    }
  }, [editMode, editTemplateId]);

  useEffect(() => {
    loadEditTemplate();
  }, [loadEditTemplate]);

  const resetForm = () => {
    setTemplateTitle("");
    setDescription("");
    setelements([makeelement()]);
    setShowPreview(false);
  };

  const updateelement = (elementId, field, value) => {
    setelements((prev) =>
      prev.map((item) => {
        if (item.id !== elementId) return item;

        if (field === "type") {
          const needsOptions = value === "radio" || value === "multiple_choice";

          return {
            ...item,
            type: value,
            options: needsOptions
              ? item.options.length > 0
                ? item.options
                : [makeOption("Option 1"), makeOption("Option 2")]
              : [],
          };
        }

        return { ...item, [field]: value };
      })
    );
  };

  const addelement = () => {
    setelements((prev) => [...prev, makeelement()]);
    setMessage("");
    setMessageType("");
  };

  const duplicateelement = (elementId) => {
    const current = elements.find((item) => item.id === elementId);
    if (!current) return;

    setelements((prev) => [
      ...prev,
      {
        ...current,
        id: `${Date.now()}-${Math.random()}`,
        element: current.element ? `${current.element} Copy` : "",
        options: current.options.map((option) => ({
          ...option,
          id: `${Date.now()}-${Math.random()}`,
        })),
      },
    ]);
  };

  const removeelement = (elementId) => {
    if (elements.length === 1) {
      showMessage("At least one element is required.", "warning");
      return;
    }

    setelements((prev) => prev.filter((item) => item.id !== elementId));
    setMessage("");
    setMessageType("");
  };

  const addOption = (elementId) => {
    setelements((prev) =>
      prev.map((item) => {
        if (item.id !== elementId) return item;
        if (item.options.length >= 10) return item;

        return {
          ...item,
          options: [
            ...item.options,
            makeOption(`Option ${item.options.length + 1}`),
          ],
        };
      })
    );
  };

  const updateOption = (elementId, optionId, value) => {
    setelements((prev) =>
      prev.map((item) => {
        if (item.id !== elementId) return item;

        return {
          ...item,
          options: item.options.map((option) =>
            option.id === optionId ? { ...option, label: value } : option
          ),
        };
      })
    );
  };

  const removeOption = (elementId, optionId) => {
    setelements((prev) =>
      prev.map((item) => {
        if (item.id !== elementId) return item;
        if (item.options.length <= 2) return item;

        return {
          ...item,
          options: item.options.filter((option) => option.id !== optionId),
        };
      })
    );
  };

  const validateTemplate = () => {
    if (!templateTitle.trim()) {
      return "Please enter the assessment template title.";
    }

    for (let i = 0; i < elements.length; i += 1) {
      const item = elements[i];

      if (!item.element.trim()) {
        return `Please enter element ${i + 1}.`;
      }

      if (item.type === "radio" || item.type === "multiple_choice") {
        if (item.options.length < 2) {
          return `Element ${i + 1} needs at least 2 choices.`;
        }

        const hasEmptyOption = item.options.some(
          (option) => !option.label.trim()
        );

        if (hasEmptyOption) {
          return `Please fill all choices in element ${i + 1}.`;
        }
      }
    }

    return "";
  };

  const buildPayload = () => {
    return {
      title: templateTitle.trim(),
      description: description.trim(),
      created_by: createdBy,
      questions: elements.map((item) => {
        const isChoice =
          item.type === "radio" || item.type === "multiple_choice";

        return {
          question: item.element.trim(),
          element: item.element.trim(),
          type: normalizeelementTypeForBackend(item.type),
          required: item.required,
          is_required: item.required ? 1 : 0,
          answers: isChoice
            ? item.options.map((option) => option.label.trim())
            : [],
          options: isChoice
            ? item.options.map((option) => ({
                label: option.label.trim(),
                answer: option.label.trim(),
              }))
            : [],
        };
      }),
    };
  };

  const handleSaveTemplate = async () => {
    const error = validateTemplate();

    if (error) {
      showMessage(error, "warning");
      return;
    }

    setSaving(true);
    setMessage("");
    setMessageType("");

    try {
      const payload = buildPayload();

      const response = editMode
        ? await axios.post(
            API_ENDPOINTS.updateTemplate(editTemplateId),
            payload,
            getAxiosConfig()
          )
        : await axios.post(
            API_ENDPOINTS.createTemplate,
            payload,
            getAxiosConfig()
          );

      if (response.data?.success === false) {
        showMessage(
          response.data?.message || "Failed to save template.",
          "warning"
        );
        return;
      }

      showMessage(
        response.data?.message ||
          (editMode
            ? "Assessment template updated successfully."
            : "Assessment template saved successfully."),
        "success"
      );

      if (!editMode) {
        resetForm();
      }
    } catch (error) {
      console.error("Save template error:", error);
      showMessage(
        error.response?.data?.message ||
          "Failed to save template. Please check backend API.",
        "warning"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loadingEdit) {
    return (
      <TmisLayout showSidebar={false}>
        <main className="template-main">
          <section className="template-card">
            Loading template for edit...
          </section>
        </main>
      </TmisLayout>
    );
  }

  return (
    <TmisLayout showSidebar={false}>
      <main className="template-main">
        <section className="template-card template-hero-card">
          <div>
            <h1>
              {editMode
                ? "Edit Assessment Template"
                : "Create Assessment Template"}
            </h1>
            <p>
              Build reusable assessment forms with unlimited elements. Radio
              and multiple-choice elements can have up to 10 choices.
            </p>
          </div>

          <div className="template-hero-actions">
            <button
              type="button"
              className="preview-btn"
              onClick={() => setShowPreview(true)}
              disabled={saving}
            >
              <FiEye /> Preview
            </button>

            <button
              type="button"
              className="save-template-btn"
              onClick={handleSaveTemplate}
              disabled={saving}
            >
              <FiSave />{" "}
              {saving
                ? "Saving..."
                : editMode
                ? "Update Template"
                : "Save Template"}
            </button>

            {editMode && (
              <button
                type="button"
                className="preview-btn"
                onClick={() => navigate("/viewPerformanceTemplate")}
                disabled={saving}
              >
                <FiArrowLeft /> Back
              </button>
            )}
          </div>
        </section>

        {message && (
          <div
            className={`template-message ${
              messageType === "success" ? "success" : "warning"
            }`}
          >
            {message}
          </div>
        )}

        <section className="template-card template-detail-card">
          <div className="template-title-row">
            <label className="template-field">
              <span>Title of the Template</span>
              <input
                value={templateTitle}
                onChange={(e) => setTemplateTitle(e.target.value)}
                placeholder="Example: Student Assessment Form"
                disabled={saving}
              />
            </label>

            <label className="template-field template-total-field">
              <span>Total Elements</span>
              <input value={`${elements.length} element(s)`} readOnly />
            </label>
          </div>

          <label className="template-field template-full-field">
            <span>Description / Instruction</span>
            <textarea
              className="template-description-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Write short instructions before answering this assessment form."
              disabled={saving}
            />
          </label>
        </section>

        <section className="template-card question-list-card">
          <div className="question-list-header">
            <div>
              <h2>Assessment Elements</h2>
              <p>{totalChoiceelements} choice element(s) in this template</p>
            </div>

            <button
              type="button"
              className="add-question-btn"
              onClick={addelement}
              disabled={saving}
            >
              <FiPlus /> Add Element
            </button>
          </div>

          <div className="question-list">
            {elements.map((item, elementIndex) => {
              const needsOptions =
                item.type === "radio" || item.type === "multiple_choice";

              return (
                <div className="question-card" key={item.id}>
                  <div className="question-card-header">
                    <h3>Element {elementIndex + 1}</h3>

                    <div className="question-actions">
                      <button
                        type="button"
                        onClick={() => duplicateelement(item.id)}
                        disabled={saving}
                      >
                        <FiCopy /> Duplicate
                      </button>

                      <button
                        type="button"
                        className="delete-question-btn"
                        onClick={() => removeelement(item.id)}
                        disabled={saving}
                      >
                        <FiTrash2 /> Delete
                      </button>
                    </div>
                  </div>

                  <label className="template-field question-field-full">
                    <span>Element</span>
                    <textarea
                      className="template-question-textarea"
                      value={item.element}
                      onChange={(e) =>
                        updateelement(item.id, "element", e.target.value)
                      }
                      placeholder="Enter your assessment evaluation element..."
                      disabled={saving}
                    />
                  </label>

                  <div className="question-settings-row">
                    <label className="template-field">
                      <span>Type</span>
                      <div className="template-select-wrapper">
                        <select
                          value={item.type}
                          onChange={(e) =>
                            updateelement(item.id, "type", e.target.value)
                          }
                          disabled={saving}
                        >
                          {element_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        <FiChevronDown />
                      </div>
                    </label>

                    <label className="required-box">
                      <input
                        type="checkbox"
                        checked={item.required}
                        onChange={(e) =>
                          updateelement(item.id, "required", e.target.checked)
                        }
                        disabled={saving}
                      />
                      <span>Required element</span>
                    </label>
                  </div>

                  {needsOptions && (
                    <div className="option-builder">
                      <div className="option-builder-header">
                        <div>
                          <h4>
                            {item.type === "radio"
                              ? "Radio Button Names"
                              : "Multiple Choice Names"}
                          </h4>
                          <p>Minimum 2 choices, maximum 10 choices.</p>
                        </div>

                        <button
                          type="button"
                          className="add-option-btn"
                          disabled={item.options.length >= 10 || saving}
                          onClick={() => addOption(item.id)}
                        >
                          <FiPlus /> Add Choice
                        </button>
                      </div>

                      <div className="option-list">
                        {item.options.map((option, optionIndex) => (
                          <div className="option-row" key={option.id}>
                            <span>{optionIndex + 1}</span>

                            <input
                              value={option.label}
                              onChange={(e) =>
                                updateOption(item.id, option.id, e.target.value)
                              }
                              placeholder={`Choice ${optionIndex + 1}`}
                              disabled={saving}
                            />

                            <button
                              type="button"
                              disabled={item.options.length <= 2 || saving}
                              onClick={() => removeOption(item.id, option.id)}
                              aria-label="Remove choice"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {showPreview && (
        <div className="template-modal-backdrop">
          <div
            className="template-preview-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="template-modal-header">
              <div>
                <h2>{templateTitle || "Untitled Assessment Template"}</h2>
                <p>{description || "No description added."}</p>
              </div>

              <button type="button" onClick={() => setShowPreview(false)}>
                <FiX />
              </button>
            </div>

            <div className="preview-question-list">
              {elements.map((item, index) => (
                <div className="preview-question" key={item.id}>
                  <h3>
                    {index + 1}. {item.element || "Untitled element"}
                    {item.required && <span>Required</span>}
                  </h3>

                  {item.type === "short_answer" && (
                    <input placeholder="Short answer" readOnly />
                  )}

                  {item.type === "long_answer" && (
                    <textarea
                      className="preview-long-answer"
                      placeholder="Long answer"
                      readOnly
                    />
                  )}

                  {(item.type === "radio" ||
                    item.type === "multiple_choice") && (
                    <div className="preview-options">
                      {item.options.map((option) => (
                        <label key={option.id}>
                          <input
                            type={item.type === "radio" ? "radio" : "checkbox"}
                            readOnly
                          />
                          {option.label || "Choice"}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </TmisLayout>
  );
};

export default PerformanceTemplate;